import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import MapboxDraw, { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { ReportGmailerrorred } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Button, Paper, Typography } from "@mui/material";
import bbox from "@turf/bbox";
import {
  ColDef,
  GetRowIdParams,
  GridOptions,
  ICellRendererParams,
  RowClassRules,
  RowClickedEvent,
  SelectionChangedEvent,
  ValueGetterParams,
} from "ag-grid-community";
import { Feature, FeatureCollection } from "geojson";

import { LoadingButton } from "ui";

import lassoSelect from "fond/draw/modes/base/lassoSelect";
import polygonSelectMode from "fond/draw/modes/base/polygonSelect";
import readOnlySelectMode from "fond/draw/modes/base/readOnlySelect";
import { MultiLayerEditLegacyProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProviderLegacy";
import { MapContext } from "fond/map/MapProvider";
import SourceAndLayers from "fond/map/SourceAndLayers";
import { GenericActionButton, PolygonSelectButton, ToolbarConfig } from "fond/map/Toolbar";
import SelectButton from "fond/map/Toolbar/SelectButton";
import { getCurrentProjectData } from "fond/project";
import { replaceLayerFeatures, selectFeature, unselectFeature } from "fond/project/redux";
import {
  buildLayerClassificationInputs,
  classifyParcelLayerInWorkerPool,
  DEFAULT_LAYER_CLASSIFICATION_CONFIG,
  findDemandsForParcels,
  type LayerClassificationResult,
} from "fond/streetParcels";
import { featureCollection } from "fond/turf";
import { isAnyLineString, isAnyPolygon, isPoint } from "fond/types/geojson";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { AgGrid, LinearProgressWithLabel, useStackedNavigationContext } from "fond/widgets";
import StreetParcelWorker from "fond/workers/streetParcels?worker";

import { OVERLAY_LAYER_CONFIG, OVERLAY_SOURCE_ID, OVERLAY_STYLES } from "./streetParcelOverlay";

import { Container, FOCUSED_ROW_CLASS } from "./IdentifyStreetParcels.styles";

const { constants: Constants } = MapboxDraw;

const PARCEL_LAYER = "input_layers/parcel";
const STREET_LAYER = "input_layers/streets";
const DEMAND_LAYER = "input_layers/demand";

// Minimum probability for which parcels will be included in the list.
const INCLUSION_FLOOR = 0.6;

/**
 * Return a human-readable label for a parcel.
 *
 * We prefer:
 * - The street address (if present), or
 * - The APN (if present), or
 * - The parcel's numeric id (as a fallback).
 */
const parcelLabel = (properties: Record<string, unknown> | null, id: number): string => {
  const props = properties ?? {};
  const address = [props.StreetNumber, props.StreetName].filter(Boolean).join(" ").trim();
  if (address) return address;
  if (typeof props.PARCELAPN === "string" && props.PARCELAPN) return props.PARCELAPN;

  return `Parcel ${id}`;
};

/** Why a parcel is listed for removal. */
type ParcelCategory = "streetParcel" | "distant" | "manual";

const PARCEL_CATEGORIES: ParcelCategory[] = ["streetParcel", "distant", "manual"];

const CATEGORY_LABEL: Record<ParcelCategory, string> = {
  streetParcel: "Street parcel",
  distant: "Distant from design",
  manual: "Manually flagged",
};

const CATEGORY_DESCRIPTION: Record<ParcelCategory, string> = {
  streetParcel: "Parcels that intersect streets. For the best underground network, remove these before running data preparation.",
  distant: "Parcels far away from your design.",
  manual: "Parcels you added to the list from the map.",
};

/** The category-specific details of a listed parcel. */
type ParcelFlag =
  | {
      category: "streetParcel";

      /** The calculated probability that this parcel is a street parcel. */
      probability: number;
    }
  | {
      category: "distant";

      /** Distance (metres) to the nearest street, or `null` if none lies within `DISTANT_HIGH_CONFIDENCE_METRES`. */
      nearestStreetMetres: number | null;
    }
  | { category: "manual" };

type StreetParcelRow = {
  /* The index of this parcel. */
  id: number;

  /** The human-readable label for the parcel. */
  label: string;

  /** The identified parcel feature. */
  parcel: Feature;
} & ParcelFlag;

type ConfidenceBand = "High" | "Medium" | "Low" | "Manual";

// street parcel bands, by model probability
const HIGH_CONFIDENCE_THRESHOLD = 0.999;
const MEDIUM_CONFIDENCE_THRESHOLD = 0.95;

// distant parcel bands, by distance to the nearest street
const DISTANT_MEDIUM_CONFIDENCE_METRES = 200;
const DISTANT_HIGH_CONFIDENCE_METRES = 500;

/* Return the confidence band for a listed parcel. */
const confidenceBand = (row: ParcelFlag): ConfidenceBand => {
  if (row.category === "manual") return "Manual";
  if (row.category === "streetParcel") {
    if (row.probability > HIGH_CONFIDENCE_THRESHOLD) return "High";
    if (row.probability >= MEDIUM_CONFIDENCE_THRESHOLD) return "Medium";
    return "Low";
  }
  if (row.nearestStreetMetres === null || row.nearestStreetMetres >= DISTANT_HIGH_CONFIDENCE_METRES) return "High";
  if (row.nearestStreetMetres >= DISTANT_MEDIUM_CONFIDENCE_METRES) return "Medium";
  return "Low";
};

const BAND_COLOR: Record<ConfidenceBand, string> = { High: "success.main", Medium: "warning.main", Low: "grey.400", Manual: "info.main" };

/** AgGrid cell renderer for a confidence range. */
const ConfidenceCell = ({ data }: ICellRendererParams<StreetParcelGridRow>): React.ReactNode => {
  if (!data) return null;
  const band = confidenceBand(data);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, height: "100%" }}>
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: BAND_COLOR[band], flexShrink: 0 }} />
      {band}
    </Box>
  );
};

/** Return how a classification result should be listed, or `null` if it shouldn't be. */
const resultFlag = (result: LayerClassificationResult): ParcelFlag | null => {
  if (result.kind === "distant") return { category: "distant", nearestStreetMetres: result.nearestStreetMetres };
  if (result.probability !== null && result.probability >= INCLUSION_FLOOR) return { category: "streetParcel", probability: result.probability };
  return null;
};

const confidenceBandSortValue = (band: ConfidenceBand): number => {
  switch (band) {
    case "High":
      return 3;
    case "Medium":
      return 2;
    case "Low":
      return 1;
    case "Manual":
      return 99;
    default:
      return 100;
  }
};

const rowCategorySortValue = (row: ParcelFlag): number => {
  switch (row.category) {
    case "streetParcel":
      return 1;
    case "distant":
      return 2;
    case "manual":
      return 3;
    default:
      return 4;
  }
};

/** Sort key: sort from low -> high confidence, as low confidence parcels are more likely to require manual review. */
const sortKey = (row: ParcelFlag): [number, number, number] => {
  const confidenceSortValue = confidenceBandSortValue(confidenceBand(row));
  const categorySortValue = rowCategorySortValue(row);

  let tieBreaker = 0;
  if (row.category === "streetParcel") tieBreaker = row.probability;
  if (row.category === "distant") tieBreaker = row.nearestStreetMetres ?? Number.MAX_SAFE_INTEGER;

  return [confidenceSortValue, categorySortValue, tieBreaker];
};

const compareRows = (a: ParcelFlag, b: ParcelFlag): number => {
  const keyA = sortKey(a);
  const keyB = sortKey(b);
  return keyA[0] - keyB[0] || keyA[1] - keyB[1] || keyA[2] - keyB[2];
};

/** A parcel row augmented with view state for the grid. */
type StreetParcelGridRow = StreetParcelRow & {
  /** Whether this parcel is the one currently highlighted on the map. */
  isFocused: boolean;
};

/**
 * The state of the identification run.
 */
type Run = { status: "idle" } | { status: "running"; progress: number } | { status: "error" } | { status: "done"; rows: StreetParcelRow[] };

const GRID_OPTIONS: GridOptions<StreetParcelGridRow> = {
  defaultColDef: { sortable: false, filter: false, resizable: false, suppressHeaderMenuButton: true },
  rowSelection: { mode: "multiRow", checkboxes: true, headerCheckbox: true, enableClickSelection: false },
  getRowId: (params: GetRowIdParams<StreetParcelGridRow>) => String(params.data.id),
  sideBar: false,
  rowGroupPanelShow: "never",
  suppressContextMenu: true,
  suppressMovableColumns: true,
  suppressCellFocus: true,
  pagination: false,
};

// Compact grid dimensions
const GRID_HEADER_HEIGHT = 31;
const GRID_ROW_HEIGHT = 25;
const MAX_VISIBLE_ROWS = 12;

// focus the row corresponding to the parcel currently selected on the map (if any)
const ROW_CLASS_RULES: RowClassRules<StreetParcelGridRow> = {
  [FOCUSED_ROW_CLASS]: (params) => Boolean(params.data?.isFocused),
};

const layerFeatures = (projectData: ReturnType<typeof getCurrentProjectData>, layerId: string): Feature[] =>
  (projectData?.layers?.[layerId]?.features ?? []) as Feature[];

const CategoryInfo: React.FC = () => (
  <Alert severity="info" variant="compact" sx={{ mb: 2 }}>
    <AlertTitle>Use this tool to remove problematic parcels:</AlertTitle>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
      {PARCEL_CATEGORIES.map((category) => (
        <Box key={category}>
          <Typography variant="inherit" fontWeight="bold">
            {CATEGORY_LABEL[category]}
          </Typography>
          <Typography variant="inherit">{CATEGORY_DESCRIPTION[category]}</Typography>
        </Box>
      ))}
    </Box>
  </Alert>
);

// Configure drawing options with layer-specific styles
const defaultOptions: MapboxDrawOptions = {
  snap: true,
  snapOptions: {
    snapVertexPriorityDistance: 0.000025,
  },
  modes: {
    simple_select: readOnlySelectMode,
    polygon_select: polygonSelectMode,
    lasso_select: lassoSelect,
  },
  editOptions: {
    allLayers: [
      {
        ID: PARCEL_LAYER,
        GeometryType: "Polygon",
        Hide: true,
      },
    ],
    editingLayerId: PARCEL_LAYER,
  },
};

const allToolConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [
      <SelectButton key="select" />,
      <PolygonSelectButton key="selectPolygon" />,
      <PolygonSelectButton key="selectLasso" selectionMode="lasso" />,
      <GenericActionButton key="genericAction" icon={<ReportGmailerrorred />} actionType="problematic" title="Add to list" />,
    ],
  },
];

/**
 * Data prep component that allows the user to identify and delete street parcels.
 */
const IdentifyStreetParcels: React.FC = () => {
  const dispatch = useAppDispatch();
  const { goBack } = useStackedNavigationContext();
  const { map } = useContext(MapContext);
  const projectData = useAppSelector((state) => getCurrentProjectData(state.project));
  const selectedFeature = useAppSelector((state) => state.project.selectedFeature);
  const versionId = useAppSelector((state) => state.project.versionId);
  const focusedParcelId = selectedFeature?.layerId === PARCEL_LAYER ? selectedFeature.feature?.id : undefined;
  const [run, setRun] = useState<Run>({ status: "idle" });
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // cancel an in-flight identification on unmount
  useEffect(() => () => abortRef.current?.abort(), []);

  const canRun = useMemo(
    () =>
      layerFeatures(projectData, PARCEL_LAYER).some(isAnyPolygon) &&
      layerFeatures(projectData, STREET_LAYER).some(isAnyLineString) &&
      layerFeatures(projectData, DEMAND_LAYER).some(isPoint),
    [projectData]
  );

  const initialParcelFeatures = useMemo(() => featureCollection(layerFeatures(projectData, PARCEL_LAYER).filter(isAnyPolygon)), [projectData]);

  const columns = useMemo<ColDef<StreetParcelGridRow>[]>(
    () => [
      { headerName: "Parcel", field: "label", flex: 1 },
      {
        headerName: "Category",
        colId: "category",
        width: 150,
        valueGetter: (params: ValueGetterParams<StreetParcelGridRow>) => (params.data ? CATEGORY_LABEL[params.data.category] : ""),
        sortable: true,
      },
      { headerName: "Confidence", colId: "confidence", width: 110, cellRenderer: ConfidenceCell },
    ],
    []
  );

  const rows = useMemo<StreetParcelGridRow[]>(
    () => (run.status === "done" ? run.rows.map((row) => ({ ...row, isFocused: focusedParcelId != null && row.parcel.id === focusedParcelId })) : []),
    [run, focusedParcelId]
  );

  const identifiedParcels = useMemo<FeatureCollection>(
    () => ({
      type: "FeatureCollection",
      features: run.status === "done" ? run.rows.filter((row) => selectedIds.has(row.id)).map((row) => row.parcel) : [],
    }),
    [run, selectedIds]
  );
  const overlaySource = useMemo(() => ({ type: "geojson", data: identifiedParcels }), [identifiedParcels]);

  const handleIdentify = async () => {
    // Abort any existing run
    abortRef.current?.abort();

    const parcelFeatures = layerFeatures(projectData, PARCEL_LAYER);
    const streetFeatures = layerFeatures(projectData, STREET_LAYER);
    const demandFeatures = layerFeatures(projectData, DEMAND_LAYER);

    const { parcels, streets, demands, parcelFeatureById } = buildLayerClassificationInputs({
      parcelFeatures,
      streetFeatures,
      demandFeatures,
    });

    // Preserve manually flagged rows so they survive re-identification
    const manualRows = run.status === "done" ? run.rows.filter((r) => r.category === "manual") : [];
    // Snapshot the current selection so we can preserve deselections for existing rows
    const previousSelectedIds = new Set(selectedIds);

    const abort = new AbortController();
    abortRef.current = abort;
    setRun({ status: "running", progress: 0 });

    try {
      const results = await classifyParcelLayerInWorkerPool(() => new StreetParcelWorker(), {
        parcels,
        streets,
        demands,
        config: {
          ...DEFAULT_LAYER_CLASSIFICATION_CONFIG,
          minProbability: INCLUSION_FLOOR,
          nearestStreetSearchMetres: DISTANT_HIGH_CONFIDENCE_METRES,
        },
        // a worker's progress message can arrive after another worker has already failed the run,
        // so only update the progress while the run is still going
        onProgress: (completed, total) =>
          setRun((prev) => (prev.status === "running" ? { status: "running", progress: total > 0 ? (completed / total) * 100 : 100 } : prev)),
        signal: abort.signal,
      });

      const resultRows = results
        // every parcel in FOND has an associated address point, however we don't keep track of this association.
        // the only guarantee we have is that the associated address point will be contained within the parcel's
        // geometry. unfortunately parcels can overlap, so this association is not completely reliable.
        //
        // here, we ultimately want to delete any street parcels (along with their associated address point).
        // if a candidate street parcel contains _more_ than one demand point, then (as explained above) we can't
        // determine the associated address. to avoid this ambiguity (<=> to avoid accidentally deleting the wrong
        // address), we restrict our attention to only those parcels that contain one or zero demand points.
        .filter((r) => r.demandIds.length <= 1)
        .flatMap((r): StreetParcelRow[] => {
          const id = Number(r.id);
          const parcel = parcelFeatureById.get(id);
          const flag = resultFlag(r);
          if (!parcel || !flag) return [];

          return [{ id, label: parcelLabel(parcel.properties as Record<string, unknown> | null, id), parcel, ...flag }];
        })
        .sort(compareRows);

      // Merge algorithm results with preserved manual rows, deduplicating by parcel feature id.
      // If the algorithm also identified a manual parcel, adopt the algorithm's category and confidence.
      // Manual parcels not found by the algorithm remain as manual entries.
      const algorithmById = new Map(resultRows.map((r) => [r.parcel.id, r]));
      const updatedManualRows = manualRows.map((r): StreetParcelRow => {
        const algorithmResult = algorithmById.get(r.parcel.id);
        return algorithmResult ? { ...algorithmResult, id: r.id, label: r.label, parcel: r.parcel } : r;
      });
      const manualParcelIds = new Set(manualRows.map((r) => r.parcel.id));
      const mergedRows = [...resultRows.filter((r) => !manualParcelIds.has(r.parcel.id)), ...updatedManualRows];

      // Auto-select newly discovered rows; preserve existing selection state for rows
      // that were already in the list (so user deselections survive re-identification).
      const previousRowIds = new Set(manualRows.map((r) => r.id));
      setSelectedIds(new Set(mergedRows.filter((r) => (previousRowIds.has(r.id) ? previousSelectedIds.has(r.id) : true)).map((r) => r.id)));
      setRun({ status: "done", rows: mergedRows });
    } catch {
      if (!abort.signal.aborted) setRun({ status: "error" });
    }
  };

  const handleSelectionChanged = (event: SelectionChangedEvent<StreetParcelRow>) => {
    setSelectedIds(new Set(event.api.getSelectedRows().map((row) => row.id)));
  };

  useEffect(() => {
    if (!map) return;

    const handleSelectionChange = (event: { actionType: string; features: Feature[] }) => {
      if (event.actionType === "problematic") {
        setRun((prev) => {
          const existingRows = prev.status === "done" ? prev.rows : [];
          const existingParcelIds = new Set(existingRows.map((r) => r.parcel.id));

          const newRows = event.features
            .filter((f) => f.id != null && !existingParcelIds.has(f.id))
            .map((f): StreetParcelRow => {
              const id = Number(f.id);
              return { id, label: parcelLabel(f.properties as Record<string, unknown> | null, id), parcel: f, category: "manual" };
            });

          if (newRows.length === 0) return prev;

          return {
            status: "done",
            rows: [...existingRows, ...newRows],
          };
        });

        setSelectedIds((prev) => {
          const next = new Set(prev);
          event.features.forEach((f) => {
            if (f.id != null) next.add(Number(f.id));
          });
          return next;
        });
      }
    };

    map.on("draw.action", handleSelectionChange);
    // eslint-disable-next-line consistent-return
    return () => {
      map.off("draw.action", handleSelectionChange);
    };
  }, [map]);

  /**
   * Monitor the map's selection state and update the selected feature in the store accordingly.
   */
  useEffect(() => {
    const updateSelection = (event: { features: Feature[] }) => {
      if (event.features.length === 1 && event.features[0].properties?.featureId) {
        dispatch(selectFeature(event.features[0]));
      } else {
        dispatch(unselectFeature());
      }
    };

    map?.on(Constants.events.SELECTION_CHANGE, updateSelection);
    return () => {
      map?.off(Constants.events.SELECTION_CHANGE, updateSelection);
    };
  }, [dispatch, map]);

  const handleFocus = (row: StreetParcelRow) => {
    if (!map) return;
    const [minX, minY, maxX, maxY] = bbox(row.parcel);
    map.fitBounds(
      [
        [minX, minY],
        [maxX, maxY],
      ],
      // animation makes the process of reviewing parcels extremely slow, so disable them
      { padding: 40, animate: false }
    );
    dispatch(selectFeature({ ...row.parcel, source: PARCEL_LAYER }));
  };

  const handleRowClicked = (event: RowClickedEvent<StreetParcelRow>) => {
    if (event.data) handleFocus(event.data);
  };

  const deletableRows = useMemo(() => rows.filter((row) => selectedIds.has(row.id)), [rows, selectedIds]);

  const handleDelete = async () => {
    if (run.status !== "done" || deletableRows.length === 0) return;

    const deletedParcelIds = new Set(deletableRows.map((row) => row.parcel.id));
    const demandFeatures = layerFeatures(projectData, DEMAND_LAYER).filter(isPoint);
    const demandsByParcel = findDemandsForParcels(deletableRows.map((r) => r.parcel).filter(isAnyPolygon), demandFeatures);
    const deletedDemandIds = new Set(deletableRows.flatMap((row) => demandsByParcel.get(row.parcel.id)?.map((f) => f.id) ?? []));

    setIsDeleting(true);
    try {
      await dispatch(
        replaceLayerFeatures({
          [PARCEL_LAYER]: layerFeatures(projectData, PARCEL_LAYER).filter((f) => !deletedParcelIds.has(f.id)),
          [DEMAND_LAYER]: layerFeatures(projectData, DEMAND_LAYER).filter((f) => !deletedDemandIds.has(f.id)),
        })
      );
      goBack();
    } finally {
      setIsDeleting(false);
    }
  };

  const gridHeight = GRID_HEADER_HEIGHT + Math.min(Math.max(rows.length, 1), MAX_VISIBLE_ROWS) * GRID_ROW_HEIGHT + 2;

  return (
    <MultiLayerEditLegacyProvider versionId={versionId} options={defaultOptions} config={allToolConfig} initialFeatures={initialParcelFeatures}>
      <Container data-testid="identify-street-parcels" sx={{ px: 1 }}>
        {run.status === "done" && (
          <SourceAndLayers
            sourceId={OVERLAY_SOURCE_ID}
            source={overlaySource}
            styles={OVERLAY_STYLES}
            layers={[OVERLAY_LAYER_CONFIG]}
            isVisible={() => true}
          />
        )}

        {(run.status === "idle" || run.status === "error") && (
          <>
            <CategoryInfo />

            {run.status === "error" && (
              <Alert severity="error" variant="compact" sx={{ mb: 2 }} data-testid="identify-street-parcels-error">
                Something went wrong while identifying parcels. Please try again.
              </Alert>
            )}

            {!canRun && (
              <Alert severity="warning" variant="compact" sx={{ mb: 2 }}>
                The parcel, street and address layers must be present to review problematic parcels.
              </Alert>
            )}
          </>
        )}

        <LoadingButton
          fullWidth
          variant="contained"
          size="small"
          onClick={handleIdentify}
          disabled={!canRun || run.status === "running"}
          loading={run.status === "running"}
          data-testid="identify-street-parcels-run"
          sx={{ mb: 2 }}
        >
          Auto identify problematic parcels
        </LoadingButton>

        {run.status === "running" && (
          <Paper variant="outlined" sx={{ py: 2, px: 2.75 }}>
            <LinearProgressWithLabel color="primary" title="Identifying problematic parcels" value={run.progress} progressBarMarginRight={0} />
          </Paper>
        )}

        {run.status === "done" && rows.length === 0 && (
          <Alert severity="success" variant="compact" data-testid="no-problematic-parcels" sx={{ mb: 2 }}>
            No problematic parcels were found.
          </Alert>
        )}

        {run.status === "done" && rows.length > 0 && <CategoryInfo />}

        {run.status !== "running" && (
          <AgGrid
            columnDefs={columns}
            rowData={rows}
            gridOptions={GRID_OPTIONS}
            rowClassRules={ROW_CLASS_RULES}
            onRowDataUpdated={(event) => {
              event.api.forEachNode((node) => {
                if (node.data) {
                  node.setSelected(selectedIds.has(node.data.id));
                }
              });
            }}
            onSelectionChanged={handleSelectionChanged}
            onRowClicked={handleRowClicked}
            containerProps={{ height: gridHeight }}
            variant="outlined"
            size="compact"
          />
        )}

        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => goBack()} disabled={isDeleting} data-testid="cancel-street-parcels">
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            size="small"
            onClick={handleDelete}
            loading={isDeleting}
            disabled={deletableRows.length === 0}
            data-testid="delete-selected-parcels"
          >
            Delete {deletableRows.length} selected {deletableRows.length === 1 ? "parcel" : "parcels"}
          </LoadingButton>
        </Box>
      </Container>
    </MultiLayerEditLegacyProvider>
  );
};

export default IdentifyStreetParcels;
